import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, AlertTriangle, Clock, Zap, TrendingDown, TrendingUp, Minus, Loader2, PartyPopper, WifiOff, Check, Target } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';
const WS_BASE = API.replace(/^http/, 'ws') || `ws://${window.location.host}`;

// Display CSS variables — always dark, optimized for warehouse
const DISPLAY_VARS = {
  '--display-bg': '#0F1117',
  '--display-card': '#1A1D2B',
  '--display-text': '#E8EAF2',
  '--display-muted': '#5A5F7A',
  '--display-success': '#10B981',
  '--display-warning': '#F59E0B',
  '--display-error': '#EF4444',
  '--display-blue': '#338edc',
};

const RESULT_DISPLAY_SECONDS = 20;

/**
 * RecordingDisplayPage — Fullscreen Feedback Display für Onboarding am Packtisch.
 * Route: /aufnahmen/display/:cameraId?token=xxx
 * Optimiert für Tablet (1024×768), dunkler Hintergrund.
 */
export default function RecordingDisplayPage() {
  const { cameraId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState('connecting'); // connecting | disconnected | waiting | recording | analysing | result | ziel_erreicht
  const [lastResult, setLastResult] = useState(null);
  const [tagesStats, setTagesStats] = useState({ auftraege: 0, avg_zeit: 0, trend: 'gleich', avg_score: 0, serie: 0, ziel_tage: 5 });
  const [visibleSteps, setVisibleSteps] = useState(0);
  const [showTipp, setShowTipp] = useState(false);
  const [scoreAnimVal, setScoreAnimVal] = useState(0);
  const [confetti, setConfetti] = useState(false);
  const [zielData, setZielData] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [resultFading, setResultFading] = useState(false);
  const [recordingAuftrag, setRecordingAuftrag] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const resultTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const statsIntervalRef = useRef(null);
  const onboardingIdRef = useRef(null);

  // Exponential backoff delays: immediate, 2s, 5s, then 10s
  const getReconnectDelay = (attempt) => {
    if (attempt === 0) return 0;
    if (attempt === 1) return 2000;
    if (attempt === 2) return 5000;
    return 10000;
  };

  // Load onboarding fortschritt for this camera's MA
  const loadOnboardingStats = useCallback(async () => {
    if (!onboardingIdRef.current) return;
    try {
      const res = await fetch(`${API}/api/recordings/onboarding/${onboardingIdRef.current}/fortschritt`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const tv = data.tages_verlauf || [];
        const today = tv.find(d => d.datum === new Date().toISOString().split('T')[0]);
        setTagesStats(prev => ({
          ...prev,
          auftraege: today?.count || prev.auftraege,
          avg_zeit: today?.avg_zeit ? parseInt(today.avg_zeit) : prev.avg_zeit,
          avg_score: today?.avg_score ? parseFloat(today.avg_score) : prev.avg_score,
          serie: data.serie || 0,
          ziel_tage: data.ziel_tage || 5,
        }));
      }
    } catch { /* ignore */ }
  }, []);

  // Tages-Stats from recordings
  const loadTagesStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/recordings?camera_id=${encodeURIComponent(cameraId)}&datum_von=${new Date().toISOString().split('T')[0]}&limit=500`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const recs = (data.data || []).filter(r => r.analyse_status === 'complete');
        const total = recs.length;
        const avgZeit = total > 0 ? recs.reduce((s, r) => s + (r.dauer_sekunden || 0), 0) / total : 0;
        const avgScore = total > 0 ? recs.reduce((s, r) => s + (r.gesamtbewertung || 0), 0) / total : 0;
        const half = Math.floor(total / 2);
        let trend = 'gleich';
        if (total >= 4) {
          const firstAvg = recs.slice(0, half).reduce((s, r) => s + (r.dauer_sekunden || 0), 0) / half;
          const secondAvg = recs.slice(half).reduce((s, r) => s + (r.dauer_sekunden || 0), 0) / (total - half);
          if (secondAvg < firstAvg * 0.95) trend = 'besser';
          else if (secondAvg > firstAvg * 1.05) trend = 'schlechter';
        }
        setTagesStats(prev => ({ ...prev, auftraege: total, avg_zeit: Math.round(avgZeit), avg_score: Math.round(avgScore * 10) / 10, trend }));
      }
    } catch { /* ignore */ }
    // Also load onboarding-specific stats
    loadOnboardingStats();
  }, [cameraId, loadOnboardingStats]);

  // Start countdown timer for result display
  const startCountdown = useCallback(() => {
    setCountdown(RESULT_DISPLAY_SECONDS);
    setResultFading(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (resultTimerRef.current) clearTimeout(resultTimerRef.current);

    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          // Start fade-out
          setResultFading(true);
          resultTimerRef.current = setTimeout(() => {
            setStatus('waiting');
            setResultFading(false);
          }, 1000);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // WebSocket connect with exponential backoff
  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = `${WS_BASE}/ws/display?token=${encodeURIComponent(token)}&camera_id=${encodeURIComponent(cameraId)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus('waiting');
      reconnectAttemptRef.current = 0;
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      loadTagesStats();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleMessage(data);
      } catch { /* ignore */ }
    };

    ws.onclose = () => {
      setStatus(prev => prev === 'ziel_erreicht' ? prev : 'disconnected');
      scheduleReconnect();
    };

    ws.onerror = () => ws.close();
  }, [cameraId, token, loadTagesStats]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) return;
    const delay = getReconnectDelay(reconnectAttemptRef.current);
    reconnectAttemptRef.current++;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connectWs();
    }, delay);
  }, [connectWs]);

  const handleMessage = useCallback((data) => {
    switch (data.typ) {
      case 'connected':
        setStatus('waiting');
        if (data.onboarding_id) onboardingIdRef.current = data.onboarding_id;
        break;
      case 'recording_started':
        setStatus('recording');
        setRecordingAuftrag(data.auftrag_id || null);
        setLastResult(null);
        setVisibleSteps(0);
        setShowTipp(false);
        setCountdown(0);
        setResultFading(false);
        if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
        if (resultTimerRef.current) { clearTimeout(resultTimerRef.current); resultTimerRef.current = null; }
        break;
      case 'recording_complete':
        setStatus('analysing');
        break;
      case 'analysing':
        setStatus('analysing');
        break;
      case 'analyse_ergebnis':
        setLastResult(data);
        setStatus('result');
        setVisibleSteps(0);
        setShowTipp(false);
        setScoreAnimVal(0);
        // Animate steps sequentially
        const stepCount = (data.schritte || []).length;
        for (let i = 0; i <= stepCount; i++) {
          setTimeout(() => setVisibleSteps(i), i * 120);
        }
        // Animate score
        const targetScore = data.bewertung || 0;
        let current = 0;
        const scoreInterval = setInterval(() => {
          current += 0.2;
          if (current >= targetScore) { current = targetScore; clearInterval(scoreInterval); }
          setScoreAnimVal(Math.round(current * 10) / 10);
        }, 30);
        // Show tipp after 2s
        setTimeout(() => setShowTipp(true), 2000);
        // Update tages stats
        loadTagesStats();
        // Start 20s countdown with bar
        startCountdown();
        break;
      case 'ziel_erreicht':
        setZielData(data);
        setStatus('ziel_erreicht');
        setConfetti(true);
        // Clear any countdown
        if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
        if (resultTimerRef.current) { clearTimeout(resultTimerRef.current); resultTimerRef.current = null; }
        setTimeout(() => setConfetti(false), 8000);
        break;
      default:
        break;
    }
  }, [loadTagesStats, startCountdown]);

  useEffect(() => {
    connectWs();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (statsIntervalRef.current) clearInterval(statsIntervalRef.current);
    };
  }, [connectWs]);

  // Polling tages stats every 60s in waiting state
  useEffect(() => {
    if (statsIntervalRef.current) clearInterval(statsIntervalRef.current);
    if (status === 'waiting') {
      statsIntervalRef.current = setInterval(loadTagesStats, 60000);
    }
    return () => { if (statsIntervalRef.current) clearInterval(statsIntervalRef.current); };
  }, [status, loadTagesStats]);

  const formatTime = (sek) => {
    if (!sek && sek !== 0) return '—';
    const m = Math.floor(sek / 60);
    const s = Math.round(sek % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const scoreColor = (s) => s >= 8 ? 'var(--display-success)' : s >= 5 ? 'var(--display-warning)' : 'var(--display-error)';

  const TrendIcon = tagesStats.trend === 'besser' ? TrendingDown : tagesStats.trend === 'schlechter' ? TrendingUp : Minus;
  const trendColor = tagesStats.trend === 'besser' ? 'var(--display-success)' : tagesStats.trend === 'schlechter' ? 'var(--display-error)' : 'var(--display-muted)';

  return (
    <div className="fixed inset-0 overflow-hidden flex flex-col" style={{ ...DISPLAY_VARS, background: 'var(--display-bg)', color: 'var(--display-text)', fontFamily: "'DM Sans', sans-serif" }}>
      {confetti && <ConfettiOverlay />}

      {/* Header */}
      <div className="flex items-center justify-between px-8 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm" style={{ background: 'var(--display-blue)' }}>W</div>
          <span className="text-lg font-bold tracking-tight">WeShip</span>
        </div>
        {recordingAuftrag && status === 'recording' && (
          <span className="text-base font-medium" style={{ color: 'var(--display-muted)' }}>Auftrag #{recordingAuftrag}</span>
        )}
        {lastResult?.auftrag_id && status === 'result' && (
          <span className="text-base font-medium" style={{ color: 'var(--display-muted)' }}>Auftrag #{lastResult.auftrag_id}</span>
        )}
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${status === 'disconnected' || status === 'connecting' ? 'animate-pulse' : ''}`}
            style={{ background: (status === 'disconnected' || status === 'connecting') ? 'var(--display-error)' : 'var(--display-success)' }} />
          <span className="text-sm" style={{ color: 'var(--display-muted)' }}>{cameraId}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-8" style={{ opacity: resultFading ? 0 : 1, transition: 'opacity 1s ease' }}>

        {/* CONNECTING */}
        {status === 'connecting' && (
          <div className="text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: 'var(--display-blue)' }} />
            <p className="text-xl" style={{ color: 'var(--display-muted)' }}>Verbindung wird hergestellt...</p>
            <p className="text-sm mt-2" style={{ color: 'rgba(255,255,255,0.2)' }}>Display verbindet sich automatisch</p>
          </div>
        )}

        {/* DISCONNECTED — Reconnecting */}
        {status === 'disconnected' && (
          <div className="text-center">
            <WifiOff className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--display-warning)' }} />
            <p className="text-xl" style={{ color: 'var(--display-warning)' }}>Verbindung unterbrochen</p>
            <p className="text-sm mt-2" style={{ color: 'var(--display-muted)' }}>wird wiederhergestellt...</p>
          </div>
        )}

        {/* WAITING */}
        {status === 'waiting' && (
          <div className="text-center w-full max-w-2xl">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(51,142,220,0.15)' }}>
              <Clock className="w-10 h-10" style={{ color: 'var(--display-blue)' }} />
            </div>
            <h2 className="text-3xl font-bold mb-2">Bereit für nächsten Auftrag</h2>
            <p className="text-lg mb-8" style={{ color: 'var(--display-muted)' }}>Video-Analyse startet automatisch</p>

            {/* Waiting-State Tages-Statistiken */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <WaitingStat label="Aufträge heute" value={tagesStats.auftraege} />
              <WaitingStat label="Ø Zeit" value={formatTime(tagesStats.avg_zeit)} />
              <WaitingStat label="Ø Score" value={tagesStats.avg_score > 0 ? tagesStats.avg_score.toFixed(1) : '—'} />
              <WaitingStat label="Serie" value={`${tagesStats.serie} / ${tagesStats.ziel_tage}`} sub="Tage unter SOLL" />
            </div>

            {/* Fortschrittsbalken zum Ziel */}
            {tagesStats.ziel_tage > 0 && (
              <div className="max-w-sm mx-auto">
                <div className="flex items-center justify-between text-xs mb-1" style={{ color: 'var(--display-muted)' }}>
                  <span>Fortschritt zum Ziel</span>
                  <span>{Math.min(100, Math.round(tagesStats.serie / tagesStats.ziel_tage * 100))}%</span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                  <div className="h-full rounded-full transition-all duration-1000" style={{
                    width: `${Math.min(100, tagesStats.serie / tagesStats.ziel_tage * 100)}%`,
                    background: 'var(--display-blue)',
                  }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* RECORDING */}
        {status === 'recording' && (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.15)' }}>
              <div className="w-5 h-5 rounded-full animate-pulse" style={{ background: 'var(--display-error)' }} />
            </div>
            <h2 className="text-3xl font-bold mb-2">Aufnahme läuft...</h2>
            <p className="text-lg" style={{ color: 'var(--display-muted)' }}>Packvorgang wird aufgezeichnet</p>
            {recordingAuftrag && <p className="text-base mt-3" style={{ color: 'var(--display-blue)' }}>#{recordingAuftrag}</p>}
          </div>
        )}

        {/* ANALYSING */}
        {status === 'analysing' && (
          <div className="text-center">
            <Loader2 className="w-16 h-16 mx-auto mb-6 animate-spin" style={{ color: 'var(--display-blue)' }} />
            <h2 className="text-3xl font-bold mb-2">Wird analysiert...</h2>
            <p className="text-lg" style={{ color: 'var(--display-muted)' }}>AI wertet den Packvorgang aus</p>
          </div>
        )}

        {/* RESULT */}
        {status === 'result' && lastResult && (
          <div className="w-full max-w-5xl flex gap-6">
            {/* Left: Schritt-Liste */}
            <div className="flex-1 rounded-2xl p-6" style={{ background: 'var(--display-card)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: 'var(--display-muted)' }}>Schritte</h3>
              <div className="space-y-2">
                {(lastResult.schritte || []).map((step, i) => {
                  if (i >= visibleSteps) return null;
                  const missing = !step.erkannt;
                  const wrongOrder = step.erkannt && !step.reihenfolge_korrekt;
                  const Icon = missing ? XCircle : wrongOrder ? AlertTriangle : CheckCircle2;
                  const color = missing ? 'var(--display-error)' : wrongOrder ? 'var(--display-warning)' : 'var(--display-success)';
                  const bgColor = missing ? 'rgba(239,68,68,0.08)' : wrongOrder ? 'rgba(245,158,11,0.08)' : 'rgba(16,185,129,0.08)';
                  const zeitDiff = step.dauer_ist_sek && step.dauer_soll_sek
                    ? Math.round((step.dauer_ist_sek - step.dauer_soll_sek) / step.dauer_soll_sek * 100) : null;
                  const zeitOverrun = zeitDiff && zeitDiff > 30;

                  return (
                    <div key={i} className="flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-300"
                      style={{ background: bgColor, animation: `slideIn 0.3s ease ${i * 0.12}s both` }}>
                      <Icon className="w-5 h-5 flex-shrink-0" style={{ color }} />
                      <span className="text-[15px] font-medium flex-1">{i + 1}. {step.name || step.schritt}</span>
                      <span className="text-sm tabular-nums w-20 text-right" style={{ color: 'var(--display-muted)' }}>
                        {step.dauer_ist_sek != null ? `${step.dauer_ist_sek}s` : '—'}
                        {' / '}
                        {step.dauer_soll_sek != null ? `${step.dauer_soll_sek}s` : '—'}
                      </span>
                      {zeitOverrun && (
                        <span className="text-xs font-bold ml-1" style={{ color: 'var(--display-error)' }}>+{zeitDiff}%</span>
                      )}
                    </div>
                  );
                })}
                {(lastResult.fehlende_schritte || []).length > 0 && visibleSteps >= (lastResult.schritte || []).length && (
                  <div className="mt-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)' }}>
                    <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: 'var(--display-error)' }}>Nicht erkannt</p>
                    {lastResult.fehlende_schritte.map((fs, i) => (
                      <p key={i} className="text-sm" style={{ color: 'rgba(239,68,68,0.8)' }}>— {fs}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Score + Zeit + Tipp */}
            <div className="w-80 space-y-5">
              <div className="rounded-2xl p-6 text-center" style={{ background: 'var(--display-card)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--display-muted)' }}>Bewertung</p>
                <div className="text-[64px] leading-none font-bold tabular-nums" style={{ color: scoreColor(scoreAnimVal) }}>
                  {scoreAnimVal.toFixed(1)}
                </div>
                <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.2)' }}>von 10</p>
              </div>

              <div className="rounded-2xl p-6" style={{ background: 'var(--display-card)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--display-muted)' }}>
                  <Clock className="w-4 h-4 inline mr-1" />Deine Zeit
                </p>
                <TempoBar ist={lastResult.gesamt_ist} soll={lastResult.gesamt_soll} formatTime={formatTime} />
              </div>

              {showTipp && lastResult.tipp && (
                <div className="rounded-2xl p-6" style={{ background: 'rgba(51,142,220,0.12)', border: '1px solid rgba(51,142,220,0.25)', animation: 'fadeIn 0.7s ease' }}>
                  <p className="text-sm font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--display-blue)' }}>
                    <Zap className="w-4 h-4 inline mr-1" />Tipp
                  </p>
                  <p className="text-lg leading-relaxed" style={{ color: 'rgba(232,234,242,0.9)' }}>{lastResult.tipp}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ZIEL ERREICHT */}
        {status === 'ziel_erreicht' && (
          <div className="text-center">
            {/* Animated green checkmark */}
            <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'var(--display-success)', animation: 'scaleIn 0.5s ease' }}>
              <Check className="w-14 h-14 text-white" strokeWidth={3} />
            </div>
            <h2 className="text-4xl font-bold mb-3" style={{ animation: 'fadeIn 1s ease 0.3s both' }}>
              Herzlichen Glückwunsch!
            </h2>
            <p className="text-2xl mb-8" style={{ color: 'var(--display-success)', animation: 'fadeIn 1s ease 0.6s both' }}>Alle Ziele erreicht!</p>
            {zielData && (
              <div className="flex gap-10 justify-center mb-8" style={{ animation: 'fadeIn 1s ease 0.9s both' }}>
                <div className="text-center">
                  <p className="text-4xl font-bold tabular-nums">{zielData.tage || '—'}</p>
                  <p className="text-sm mt-1" style={{ color: 'var(--display-muted)' }}>Tage</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold tabular-nums">{zielData.videos || '—'}</p>
                  <p className="text-sm mt-1" style={{ color: 'var(--display-muted)' }}>Videos</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold tabular-nums">{zielData.avg_score ? zielData.avg_score.toFixed(1) : '—'}</p>
                  <p className="text-sm mt-1" style={{ color: 'var(--display-muted)' }}>Ø Score</p>
                </div>
              </div>
            )}
            <p className="text-lg mb-4" style={{ color: 'var(--display-muted)', animation: 'fadeIn 1s ease 1.2s both' }}>
              Das Feedback-Display wird deaktiviert.
            </p>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.25)', animation: 'fadeIn 1s ease 1.5s both' }}>
              Bei Fragen wende dich an deinen Teamleiter
            </p>
          </div>
        )}
      </div>

      {/* Footer: Tages-Stats + Countdown */}
      {(status === 'waiting' || status === 'result') && (
        <div className="px-8 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <span className="text-sm" style={{ color: 'var(--display-muted)' }}>Heute:</span>
              <span className="text-sm font-medium">{tagesStats.auftraege} Aufträge</span>
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.15)' }}>|</span>
              <span className="text-sm font-medium">Ø {formatTime(tagesStats.avg_zeit)}</span>
              {tagesStats.avg_score > 0 && (
                <>
                  <span className="text-sm" style={{ color: 'rgba(255,255,255,0.15)' }}>|</span>
                  <span className="text-sm font-medium">Ø Score: {tagesStats.avg_score.toFixed(1)}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2" style={{ color: trendColor }}>
              <TrendIcon className="w-4 h-4" />
              <span className="text-sm font-medium">
                {tagesStats.trend === 'besser' ? 'besser' : tagesStats.trend === 'schlechter' ? 'schlechter' : 'gleichbleibend'}
              </span>
            </div>
          </div>

          {/* Countdown bar for result display */}
          {status === 'result' && countdown > 0 && (
            <div className="mt-2 h-[3px] rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-full rounded-full transition-[width] duration-1000 ease-linear" style={{
                width: `${(countdown / RESULT_DISPLAY_SECONDS) * 100}%`,
                background: 'var(--display-blue)',
              }} />
            </div>
          )}
        </div>
      )}

      {/* Inline CSS animations */}
      <style>{`
        @keyframes slideIn { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
      `}</style>
    </div>
  );
}

// ── Waiting State Stat Card ──
function WaitingStat({ label, value, sub }) {
  return (
    <div className="rounded-xl p-4 text-center" style={{ background: 'var(--display-card)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="text-[28px] font-bold tabular-nums leading-none">{value}</p>
      {sub && <p className="text-[11px] mt-1" style={{ color: 'var(--display-muted)' }}>{sub}</p>}
      <p className="text-xs mt-1" style={{ color: 'var(--display-muted)' }}>{label}</p>
    </div>
  );
}

// ── Tempo-Balken Component ──
function TempoBar({ ist, soll, formatTime }) {
  if (!soll) return <p className="text-sm" style={{ color: 'var(--display-muted)' }}>Kein SOLL-Video</p>;
  const pct = Math.min((ist || 0) / soll * 100, 200);
  const barColor = pct <= 100 ? 'var(--display-success)' : pct <= 130 ? 'var(--display-warning)' : 'var(--display-error)';

  return (
    <div>
      <div className="text-[48px] leading-none font-bold tabular-nums text-center" style={{ color: barColor }}>
        {formatTime(ist)}
      </div>
      <p className="text-sm text-center mt-1" style={{ color: 'var(--display-muted)' }}>Ziel: {formatTime(soll)}</p>
      <div className="mt-4 relative h-4 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: barColor }} />
        <div className="absolute top-0 h-full w-0.5" style={{ left: `${Math.min(100 / (pct > 100 ? pct / 100 : 1), 100)}%`, background: 'rgba(255,255,255,0.5)' }} />
      </div>
      <div className="flex justify-between mt-1 text-[11px]" style={{ color: 'rgba(255,255,255,0.2)' }}>
        <span>0:00</span>
        <span>{formatTime(soll)}</span>
      </div>
    </div>
  );
}

// ── CSS-Only Confetti Overlay ──
function ConfettiOverlay() {
  const colors = ['#338edc', '#22D3EE', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#EC4899'];
  const pieces = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 3,
    duration: 2 + Math.random() * 3,
    color: colors[i % colors.length],
    size: 4 + Math.random() * 8,
    rotation: Math.random() * 360,
  }));

  return (
    <div className="fixed inset-0 z-50 pointer-events-none overflow-hidden">
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
      {pieces.map(p => (
        <div key={p.id} style={{
          position: 'absolute', left: `${p.left}%`, top: 0,
          width: p.size, height: p.size * 1.5,
          backgroundColor: p.color,
          borderRadius: 2,
          animation: `confettiFall ${p.duration}s ease ${p.delay}s both`,
          transform: `rotate(${p.rotation}deg)`,
        }} />
      ))}
    </div>
  );
}
